import React, { useEffect } from 'react';

import { Button } from '@/components/ui/button';
import { Sparkles } from 'lucide-react';

import { isAutomatedLayerStatsSection } from 'components/DisasterGPT/AutomatedLayerStatsSectionPanel';
import { SitrepSectionType } from 'components/DisasterGPT/hooks/useSitrepsV2';
import { ReportsGeneratingPlaceholder } from 'components/Reports/ReportsGeneratingPlaceholder';
import { SectionLastUpdatedLabel } from 'components/Reports/section/SectionLastUpdatedLabel';
import DynamicSectionDataToggle from 'components/Reports/section/DynamicSectionDataToggle';
import { useDynamicSectionData } from 'components/Reports/section/useDynamicSectionData';
import {
  SitrepSectionBody,
  useSitrepSectionEditor,
} from 'components/Reports/section/SitrepSectionBody';
import type { GetSitrepsResponse } from 'components/Reports/reports.types';
import { AnalyticsCategoryLayerPicker } from 'components/Reports/document/AnalyticsLayerActions';

interface ReportsSectionDisplayProps {
  section: SitrepSectionType;
  sitrepId: string;
  sitrep?: Pick<
    GetSitrepsResponse,
    'aor_id' | 'incident_id' | 'approval_status'
  >;
  sitrepWorkspaceId: string | null;
  sitrepTemplateId?: string | null;
  childSections?: SitrepSectionType[];
  onUpdated?: () => void;
  canEdit?: boolean;
  onAIGenerate?: (section: SitrepSectionType) => void;
}

const ReportsSectionLeaf: React.FC<
  Omit<ReportsSectionDisplayProps, 'childSections'> & {
    /** Child sections show the timestamp in their card header instead. */
    showLastUpdated?: boolean;
  }
> = ({
  section,
  sitrepId,
  sitrep,
  sitrepWorkspaceId,
  sitrepTemplateId = null,
  onUpdated,
  canEdit = false,
  showLastUpdated = true,
}) => {
  const { editor } = useSitrepSectionEditor({
    section,
    sitrepId,
    sitrepWorkspaceId,
    canEdit,
    onUpdated,
  });
  const dynamicData = useDynamicSectionData({
    section,
    sitrepId,
    approvalStatus: sitrep?.approval_status,
  });

  const { enableEditing, isEditing, cancelEditing } = editor;
  useEffect(() => {
    if (!enableEditing && isEditing) {
      cancelEditing();
    }
  }, [cancelEditing, enableEditing, isEditing]);

  return (
    <>
      {showLastUpdated && (
        <SectionLastUpdatedLabel
          section={section}
          className="tw:mb-1.5 tw:block"
        />
      )}
      {dynamicData.showLocalToggle ? (
        <div className="tw:mb-2">
          <DynamicSectionDataToggle
            approvalStatus={sitrep?.approval_status}
            mode={dynamicData.mode}
            onModeChange={dynamicData.setMode}
            disabled={dynamicData.isPreviewLoading}
          />
        </div>
      ) : null}
      {dynamicData.isPreviewLoading &&
      !(
        isAutomatedLayerStatsSection(section) &&
        dynamicData.showToggle &&
        dynamicData.mode === 'live'
      ) ? (
        <ReportsGeneratingPlaceholder
          message={
            isAutomatedLayerStatsSection(section)
              ? 'PRATUS is analyzing data...'
              : undefined
          }
        />
      ) : (
        <SitrepSectionBody
          section={section}
          sitrepId={sitrepId}
          sitrep={sitrep}
          sitrepWorkspaceId={sitrepWorkspaceId}
          sitrepTemplateId={sitrepTemplateId}
          canEdit={canEdit}
          editor={editor}
          showInlineSaveActions
          dataStringOverride={
            dynamicData.showToggle ? dynamicData.dataString : undefined
          }
          hideRun={dynamicData.hideRun}
          useLiveEnrichment={dynamicData.useLiveEnrichment}
          refreshingAnalytics={
            isAutomatedLayerStatsSection(section) &&
            dynamicData.showToggle &&
            dynamicData.mode === 'live' &&
            dynamicData.isPreviewLoading
          }
        />
      )}
    </>
  );
};

const ReportsSectionDisplay: React.FC<ReportsSectionDisplayProps> = ({
  section,
  sitrepId,
  sitrep,
  sitrepWorkspaceId,
  sitrepTemplateId = null,
  childSections = [],
  onUpdated,
  canEdit = false,
  onAIGenerate,
}) => {
  if (childSections.length > 0) {
    return (
      <div className="reports-section tw:space-y-4">
        <AnalyticsCategoryLayerPicker
          parent={section}
          childSections={childSections}
        />
        {childSections.map((child) => (
          <div
            key={child.id}
            className="tw:rounded-md tw:border tw:border-border tw:p-3"
          >
            <div className="tw:mb-2 tw:flex tw:items-center tw:justify-between tw:gap-2">
              <div className="tw:flex tw:min-w-0 tw:flex-wrap tw:items-baseline tw:gap-x-2">
                <h6 className="tw:m-0 tw:text-sm tw:font-semibold">
                  {child.name}
                </h6>
                <SectionLastUpdatedLabel section={child} />
              </div>
              {canEdit && onAIGenerate && child.type === 'ai' ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => onAIGenerate(child)}
                >
                  <Sparkles />
                  AI Generate
                </Button>
              ) : null}
            </div>
            <ReportsSectionLeaf
              section={child}
              sitrepId={sitrepId}
              sitrep={sitrep}
              sitrepWorkspaceId={sitrepWorkspaceId}
              sitrepTemplateId={sitrepTemplateId}
              onUpdated={onUpdated}
              canEdit={canEdit}
              onAIGenerate={onAIGenerate}
              showLastUpdated={false}
            />
          </div>
        ))}
      </div>
    );
  }

  return (
    <ReportsSectionLeaf
      section={section}
      sitrepId={sitrepId}
      sitrep={sitrep}
      sitrepWorkspaceId={sitrepWorkspaceId}
      sitrepTemplateId={sitrepTemplateId}
      onUpdated={onUpdated}
      canEdit={canEdit}
      onAIGenerate={onAIGenerate}
    />
  );
};

export default ReportsSectionDisplay;
